import type { VarianceCalculation, DailySalesVariance, OvernightVariance } from './types.js';

/**
 * Discrepancy thresholds in GRAMS (per CLAUDE.md)
 * < 1g = ACCEPTABLE, 1-2g = WARNING, > 2g = CRITICAL
 */
export const VARIANCE_THRESHOLDS = {
  WARNING: 1,   // >= 1g triggers warning
  CRITICAL: 2,  // > 2g triggers critical
} as const;

/**
 * Check if a product accumulates naturally (e.g., STEMS)
 * These are exempt from variance warnings
 */
export function isAccumulatingProduct(productName: string): boolean {
  return productName.toUpperCase().includes('STEM');
}

/**
 * Get variance status based on gram thresholds
 * @param variance - The variance value in grams (absolute value will be used)
 * @param productName - Optional product name to check for exemptions
 */
export function getVarianceStatus(
  variance: number,
  productName?: string
): 'ACCEPTABLE' | 'WARNING' | 'CRITICAL' {
  // STEMS always OK - they accumulate throughout the day
  if (productName && isAccumulatingProduct(productName)) {
    return 'ACCEPTABLE';
  }

  const absVariance = Math.abs(variance);
  if (absVariance < VARIANCE_THRESHOLDS.WARNING) return 'ACCEPTABLE';
  if (absVariance <= VARIANCE_THRESHOLDS.CRITICAL) return 'WARNING';
  return 'CRITICAL';
}

/**
 * Calculate daily sales variance
 * Compares physical stock movement against recorded sales
 *
 * Formula:
 *   movement = morning + restocks - evening (what physically left the shelf)
 *   variance = movement - recordedSales (positive = shrinkage/loss)
 *
 * @param morning - Morning count quantity
 * @param evening - Evening count quantity
 * @param restocks - Total restocks during the day
 * @param recordedSales - Sales recorded in POS system
 * @param productName - Optional product name for threshold exemptions
 */
export function calculateDailySalesVariance(
  morning: number,
  evening: number,
  restocks: number,
  recordedSales: number,
  productName?: string
): DailySalesVariance {
  // Movement = what physically left the shelf
  let movement = morning + restocks - evening;

  // For accumulating products (e.g., STEMS), stock GROWS during the day
  // so evening > morning is expected. Flip the sign so +X means gained.
  if (productName && isAccumulatingProduct(productName)) {
    movement = -movement;
  }

  // Variance = discrepancy between movement and recorded sales
  // Positive = shrinkage (sold more than recorded)
  // Negative = data error (recorded more than sold)
  const variance = movement - recordedSales;

  const status = getVarianceStatus(variance, productName);

  return {
    movement,
    recordedSales,
    variance,
    status,
  };
}

/**
 * Calculate overnight variance
 * Compares morning count against previous evening + overnight restocks
 * Any non-zero variance is CRITICAL (stock can't appear/disappear overnight)
 *
 * Formula:
 *   expected = prevEvening + overnightRestocks
 *   variance = morningCount - expected
 *
 * @param morningCount - This morning's count
 * @param prevEveningCount - Previous evening's count
 * @param overnightRestocks - Any restocks between evening and morning
 * @param productName - Optional product name (STEMS exempt)
 */
export function calculateOvernightVariance(
  morningCount: number,
  prevEveningCount: number,
  overnightRestocks: number,
  productName?: string
): OvernightVariance {
  const expected = prevEveningCount + overnightRestocks;
  const variance = morningCount - expected;

  // For overnight variance, any non-zero is CRITICAL (except STEMS)
  let status: 'ACCEPTABLE' | 'CRITICAL' = 'ACCEPTABLE';
  if (Math.abs(variance) > 0.01) { // Allow tiny rounding errors
    status = productName && isAccumulatingProduct(productName) ? 'ACCEPTABLE' : 'CRITICAL';
  }

  return {
    expected,
    actual: morningCount,
    variance,
    status,
  };
}

/**
 * @deprecated Use calculateDailySalesVariance instead
 * Kept for backward compatibility - will be removed in future version
 *
 * NOTE: This function had incorrect formula. New functions use correct formulas.
 */
export function calculateVariance(
  morningCount: number,
  eveningCount: number,
  sales: number,
  reUps: number
): VarianceCalculation {
  // Use the new correct calculation internally
  const result = calculateDailySalesVariance(morningCount, eveningCount, reUps, sales);

  // Map to old return format for compatibility
  const expectedQuantity = morningCount + reUps - sales;
  const actualQuantity = eveningCount;
  const varianceQuantity = result.variance; // Now uses correct sign convention
  const variancePercentage = expectedQuantity === 0
    ? 0
    : Math.abs((varianceQuantity / expectedQuantity) * 100);

  return {
    expectedQuantity,
    actualQuantity,
    varianceQuantity,
    variancePercentage,
    status: result.status,
  };
}

export function generateBusinessCode(): string {
  return Math.random().toString(16).substring(2, 8).toUpperCase();
}

/**
 * Get South African date (UTC+2) in YYYY-MM-DD format
 * Single source of truth for SA timezone date handling
 *
 * @param daysOffset - Number of days to subtract from today (default 0)
 * @returns Date string in YYYY-MM-DD format
 */
export function getSouthAfricanDate(daysOffset = 0): string {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(now);
  const year = parts.find((p) => p.type === 'year')?.value ?? String(new Date().getFullYear());
  const month = parts.find((p) => p.type === 'month')?.value ?? '01';
  const day = parts.find((p) => p.type === 'day')?.value ?? '01';

  // Create date string in YYYY-MM-DD format
  const saDate = new Date(`${year}-${month}-${day}`);
  saDate.setDate(saDate.getDate() - daysOffset);

  const offsetYear = saDate.getFullYear();
  const offsetMonth = String(saDate.getMonth() + 1).padStart(2, '0');
  const offsetDay = String(saDate.getDate()).padStart(2, '0');

  return `${offsetYear}-${offsetMonth}-${offsetDay}`;
}

/**
 * Check if a given date string is today in South African timezone
 * @param dateStr - Date string in YYYY-MM-DD format
 */
export function isTodaySA(dateStr: string): boolean {
  return dateStr === getSouthAfricanDate(0);
}

/**
 * Convert a Date object to SA date string (YYYY-MM-DD)
 */
export function toSouthAfricanDateString(date: Date): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value ?? String(date.getFullYear());
  const month = parts.find((p) => p.type === 'month')?.value ?? '01';
  const day = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

export async function validateTelegramHash(
  initData: string,
  botToken: string
): Promise<boolean> {
  const crypto = await import('crypto');

  const urlParams = new URLSearchParams(initData);
  const hash = urlParams.get('hash');
  urlParams.delete('hash');

  const dataCheckString = Array.from(urlParams.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join('\n');

  const secretKey = crypto.createHmac('sha256', 'WebAppData').update(botToken).digest();
  const calculatedHash = crypto.createHmac('sha256', secretKey).update(dataCheckString).digest('hex');

  return hash === calculatedHash;
}
